/**
 * Phone notifications, after the Watch Dogs HUD: a hard-edged banner with a
 * solid label bar (sending app, real local time), monospace data lines and a
 * key-prompt action. Read out politely to assistive tech; each leaves on its
 * own after a few seconds (held while hovered or focused) along the path it
 * came in. An optional action makes the card a button.
 */

import { play } from './sound';

export interface Notice {
  /** Sending app, shown as the card's caps label. */
  app: string;
  title: string;
  body?: string;
  action?: () => void;
  /** The action's prompt text, e.g. "Open log". */
  actionLabel?: string;
}

const LIFETIME = 6000;
const MAX = 3;
let stack: HTMLElement | null = null;

function host(): HTMLElement {
  if (stack?.isConnected) return stack;
  stack = document.createElement('div');
  stack.className = 'xw-notices';
  stack.setAttribute('role', 'status');
  stack.setAttribute('aria-live', 'polite');
  document.body.appendChild(stack);
  return stack;
}

export function notify(notice: Notice): void {
  const root = host();
  const card = document.createElement(notice.action ? 'button' : 'div');
  card.className = 'xw-notice';
  if (card instanceof HTMLButtonElement) card.type = 'button';
  const part = (cls: string, text: string, parent: HTMLElement) => {
    const el = document.createElement('span');
    el.className = cls;
    el.textContent = text;
    parent.appendChild(el);
    return el;
  };
  const head = part('xw-notice-head', '', card);
  part('xw-notice-app', notice.app, head);
  part('xw-notice-time', new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }), head);
  const main = part('xw-notice-main', '', card);
  part('xw-notice-title', notice.title, main);
  if (notice.body) part('xw-notice-body', notice.body, main);
  if (notice.action) {
    const prompt = part('xw-notice-prompt', '', main);
    part('xw-notice-key', '\u203a', prompt).setAttribute('aria-hidden', 'true');
    prompt.append(notice.actionLabel ?? 'Open');
  }

  let timer = 0;
  const leave = () => {
    window.clearTimeout(timer);
    card.classList.add('xw-notice--out');
    window.setTimeout(() => card.remove(), 180);
  };
  const arm = () => { window.clearTimeout(timer); timer = window.setTimeout(leave, LIFETIME); };
  card.addEventListener('pointerenter', () => window.clearTimeout(timer));
  card.addEventListener('pointerleave', arm);
  card.addEventListener('focusin', () => window.clearTimeout(timer));
  card.addEventListener('focusout', arm);
  if (notice.action) card.addEventListener('click', () => { leave(); notice.action?.(); });

  root.appendChild(card);
  play('notify');
  while (root.children.length > MAX) root.firstElementChild?.remove();
  arm();
}
